import dayjs from 'dayjs';
import _ from 'lodash';
import { store } from '@/store';
import {
    CustomersController,
    ICustomersGetResponse,
    ICustomersInsuranceManualResponse,
    ICustomersTestInsuranceResponse
} from '../endpoints/customers';
import { ITestClaimTransferPrescriptions, ITransferPrescriptions } from '../types/interfaces/transfer-prescriptions';
import { IProvider } from '../types/interfaces/provider';
import { getCopayEligiblePrescriptions } from './eligibility';
import { copayMaxRequestLimit } from '../constants/test-claim';

export const getPriceEligibleOtc = (prescriptions?: ITransferPrescriptions[]) => prescriptions?.filter(item => item.otc) || [];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TestClaimStatus =
    | 'PAID' // approved with a known dollar copay
    | 'REJECTED_RETRYABLE' // FILL_TOO_SOON — re-run when retryNotBefore passes or on explicit user request
    | 'REJECTED_PERMANENT' // any reject code that is not FILL_TOO_SOON
    | 'ERROR'; // server error — always re-run

export interface ITestClaimInsuranceResult {
    index: number;
    rejectCodes: string[];
    responseStatus?: string;
    coverageType?: string;
    otherCoverageCode?: string;
    additionalMessage?: unknown;
}

export interface ITestClaimSlot {
    coPay: number | null;
    status: TestClaimStatus;
    rejectCodes: string[]; // cumulative reject codes across all insurances
    retryNotBefore?: string; // ISO date — only present when FILL_TOO_SOON and a date could be parsed
    insurances?: ITestClaimInsuranceResult[]; // absent when status === 'ERROR'
    copayQuantity?: number; // quantity used for this test claim
    copayDaysSupply?: number; // days supply used for this test claim
}

// Outer key: sorted insurance IDs joined by ':', e.g. "123:456:789"
// Inner key: "<medId>:<copayQuantity>:<copayDaysSupply>", e.g. "MED555:30:30"
export type ITestClaimsByCombo = Record<string, Record<string, ITestClaimSlot>>;

// ---------------------------------------------------------------------------
// Hard pharmacy-level reject codes (same set as rejectCodesToHalt in eligibility.ts)
// ---------------------------------------------------------------------------

const rejectCodesToHalt = new Set([
    'PHARMACY_NOT_CONTRACTED_WITH_PLAN_PROCESSOR_ON_DATE_OF_SERVICE',
    'PHARMACY_NOT_CONTRACTED_IN_ASSISTED_LIVING_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_RETAIL_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_MAIL_ORDER_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_HOSPICE_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_VETERANS_ADMINISTRATION_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_MILITARY_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_SPECIALTY_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_HOME_INFUSION_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_LONG_TERM_CARE_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_90_DAY_RETAIL_NETWORK',
    'NON_MATCHED_PHARMACY_NUMBER',
    'PHARMACY_NOT_ENROLLED_IN_STATE_MEDICAID_PROGRAM',
    'MISSING_SERVICE_PROVIDER_NUMBER'
]);

// ---------------------------------------------------------------------------
// Key helpers
// ---------------------------------------------------------------------------

export const buildComboKey = (insurances: ICustomersInsuranceManualResponse[]): string =>
    insurances
        .map(i => i.id)
        .sort((a, b) => a - b)
        .join(':');

export const buildSlotKey = (
    prescription: ITestClaimTransferPrescriptions,
    prescriptionCopayQuantities?: Record<string, { quantity: number; daysSupply: number }>
): string => {
    const medId = prescription.medid;
    const override = prescriptionCopayQuantities?.[String(medId)];
    let copayQuantity = override?.quantity ?? prescription.copayQuantity ?? 30;
    let copayDaysSupply = override?.daysSupply ?? prescription.copayDaysSupply ?? 30;
    if (copayDaysSupply === 90) {
        copayQuantity = Math.ceil(copayQuantity / 3);
        copayDaysSupply = 30;
    }
    return `${medId}:${copayQuantity}:${copayDaysSupply}`;
};

// ---------------------------------------------------------------------------
// retryNotBefore extraction
// ---------------------------------------------------------------------------

const tryParseRetryDate = (additionalMessage: unknown): string | undefined => {
    if (!additionalMessage || typeof additionalMessage !== 'object') return undefined;

    const msg = additionalMessage as {
        additionalMessage?: string | null;
        nextAvailableFillDate?: string | null;
    };
    const raw = msg.nextAvailableFillDate;
    if (!raw) return undefined;

    // YYYYMMDD
    const yyyymmdd = raw.match(/\b(\d{8})\b/);
    if (yyyymmdd) {
        const parsed = dayjs(yyyymmdd[1], 'YYYYMMDD', true);
        if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
    }

    // YYYY-MM-DD
    const isoDate = raw.match(/\b(\d{4}-\d{2}-\d{2})\b/);
    if (isoDate) {
        const parsed = dayjs(isoDate[1], 'YYYY-MM-DD', true);
        if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
    }

    // MM/DD/YYYY
    const mdyDate = raw.match(/\b(\d{1,2}\/\d{1,2}\/\d{4})\b/);
    if (mdyDate) {
        const parsed = dayjs(mdyDate[1], 'MM/DD/YYYY', true);
        if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
    }

    // MM/DD/YY
    const mdyShortDate = raw.match(/\b(\d{1,2}\/\d{1,2}\/\d{2})\b/);
    if (mdyShortDate) {
        const parsed = dayjs(mdyShortDate[1], 'MM/DD/YY', true);
        if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
    }

    return undefined;
};

// ---------------------------------------------------------------------------
// Map a fill response to a ITestClaimSlot, storing results per insurance
// ---------------------------------------------------------------------------
type FillItem = ICustomersTestInsuranceResponse['patient']['fills'][number];

const mapFillToSlot = (fill: FillItem): ITestClaimSlot => {
    const insurances: ITestClaimInsuranceResult[] = fill.insurances.map((ins, index) => {
        const entry: ITestClaimInsuranceResult = {
            index,
            rejectCodes: (ins.rejectCodes ?? []) as string[]
        };
        if (ins.responseStatus != null) entry.responseStatus = ins.responseStatus as string;
        if ((ins as unknown as { coverageType?: string }).coverageType != null)
            entry.coverageType = (ins as unknown as { coverageType?: string }).coverageType;
        if ((ins as unknown as { otherCoverageCode?: string }).otherCoverageCode != null)
            entry.otherCoverageCode = (ins as unknown as { otherCoverageCode?: string }).otherCoverageCode;
        if (ins.additionalMessage != null) entry.additionalMessage = ins.additionalMessage;
        return entry;
    });

    const allRejectCodes = insurances.flatMap(i => i.rejectCodes);
    const hasNonFillTooSoon = allRejectCodes.some(code => code !== 'FILL_TOO_SOON');

    if (hasNonFillTooSoon) {
        return { coPay: fill.coPay ?? null, status: 'REJECTED_PERMANENT', rejectCodes: allRejectCodes, insurances };
    }

    const isFillTooSoon = allRejectCodes.includes('FILL_TOO_SOON');

    if (isFillTooSoon) {
        const slot: ITestClaimSlot = {
            coPay: null,
            status: 'REJECTED_RETRYABLE',
            rejectCodes: allRejectCodes,
            insurances
        };
        for (const ins of fill.insurances) {
            if (!(ins.rejectCodes as string[])?.includes('FILL_TOO_SOON')) continue;
            const retryDate = tryParseRetryDate(ins?.additionalMessage);
            if (retryDate) {
                slot.retryNotBefore = retryDate;
                break;
            }
        }
        return slot;
    }

    if (fill.coPay != null) {
        return { coPay: fill.coPay, status: 'PAID', rejectCodes: allRejectCodes, insurances };
    }

    return { coPay: null, status: 'REJECTED_PERMANENT', rejectCodes: allRejectCodes, insurances };
};

// ---------------------------------------------------------------------------
// Cache decision helpers
// ---------------------------------------------------------------------------

const shouldRerun = (slot: ITestClaimSlot): boolean => {
    if (slot.status === 'ERROR') return true;
    if (slot.status === 'PAID' || slot.status === 'REJECTED_PERMANENT') return false;
    // REJECTED_RETRYABLE (FILL_TOO_SOON)
    if (slot.status === 'REJECTED_RETRYABLE' && slot.retryNotBefore) {
        return !dayjs().isBefore(dayjs(slot.retryNotBefore, 'YYYY-MM-DD'));
    }
    return false; // no retryNotBefore — re-run
};

// Suspected incorrect insurance input codes (same set as in eligibility.ts)
const suspectedIncorrectInsuranceInputCodes = new Set([
    'NON_MATCHED_CARDHOLDER_ID',
    'MISSING_GROUP_ID',
    'MISSING_BIN_NUMBER',
    'MISSING_DATE_OF_BIRTH'
]);

const hasSuspectedIncorrectInsuranceInputs = (slot: ITestClaimSlot): boolean =>
    slot.rejectCodes.some(code => suspectedIncorrectInsuranceInputCodes.has(code));

// ---------------------------------------------------------------------------
// Situation 1: eligibility gate
// Pass = pharmacy CAN fill (or server error — don't block).
// Fail = hard pharmacy-network rejection only.
// ---------------------------------------------------------------------------

export const isEligibleForPharmacy = (slot: ITestClaimSlot, insuranceUploadsLength = 0): boolean => {
    if ((insuranceUploadsLength || 0) > 0) return true;

    const hasPaidInsurance = slot.insurances?.some(insurance => insurance.responseStatus?.toUpperCase() === 'PAID');
    if (hasPaidInsurance) return true;

    if (slot.status === 'PAID') return true;
    if (slot.status === 'ERROR') return true;
    if (slot.rejectCodes.length > 0 && slot.rejectCodes.every(code => code === 'FILL_TOO_SOON')) return true;
    return !slot.rejectCodes.some(code => rejectCodesToHalt.has(code));
};

// ---------------------------------------------------------------------------
// Request limit
// ---------------------------------------------------------------------------

export const isCopayRequestLimitReached = (customer?: ICustomersGetResponse | null): boolean => {
    const user = customer ?? store.getState().onboarding.user;
    const count = (user?.userOnboarding?.details as unknown as { copayRequestCount?: number })?.copayRequestCount ?? 0;
    return count >= copayMaxRequestLimit;
};

// ---------------------------------------------------------------------------
// canRunMoreClaims
// Finds the claim slots for the user's current insurance combo and returns
// true if it makes sense to request more/full copay claim results:
//   (any slot is approved OR has non-halting reject codes)
//   AND no slot has suspected incorrect insurance inputs
// ---------------------------------------------------------------------------

export const canRunMoreClaims = (customer?: ICustomersGetResponse | null): boolean => {
    const user = customer ?? store.getState().onboarding.user;
    if (!user?.userOnboarding || !user.insurances.length) return false;
    if ((user.insuranceUploads?.length ?? 0) > 0) return false;

    const comboKey = buildComboKey(user.insurances);
    const testClaimsByCombo = (user.userOnboarding.details as unknown as { testClaimsByCombo?: ITestClaimsByCombo })?.testClaimsByCombo;
    const relevantSlots = testClaimsByCombo?.[comboKey];

    if (!relevantSlots || !Object.keys(relevantSlots).length) return false;

    const slot = Object.values(relevantSlots)[0];
    if (!slot) return false;
    if (!Array.isArray(slot.insurances) || slot.insurances.length === 0) return false;

    return (
        (slot.coPay !== null && slot.rejectCodes.length === 0) ||
        (slot.rejectCodes.length > 0 &&
            slot.rejectCodes.every(code => code === 'FILL_TOO_SOON'))
    );
};

// ---------------------------------------------------------------------------
// Low-level: dispatch a single test claim for a list of prescriptions.
// The insurance id/npi/spi come from the first supported insurance on the user's
// profile — the server resolves coverage against all the user's insurances.
// ---------------------------------------------------------------------------

const runTestClaimForPrescriptions = async (
    prescriptions: ITestClaimTransferPrescriptions[],
    prescriptionCopayQuantities?: Record<string, { quantity: number; daysSupply: number }>,
    requestInsurances?: ICustomersInsuranceManualResponse[]
): Promise<{ slots: ITestClaimSlot[]; batchCount: number }> => {
    if (!prescriptions.length) return { slots: [], batchCount: 0 };

    // Normalise 90-day quantities for the request
    const normalised = _.cloneDeep(prescriptions).map(item => {
        // Apply caller-supplied copay quantity overrides (keyed by medid)
        const override = prescriptionCopayQuantities?.[String(item.medid)];
        if (override) {
            item.copayQuantity = override.quantity;
            item.copayDaysSupply = override.daysSupply;
        }
        if ((item.copayDaysSupply ?? 30) === 90) {
            item.copayQuantity = Math.ceil((item.copayQuantity ?? 30) / 3);
            item.copayDaysSupply = 30;
        }
        return item;
    });

    // Split into batches of 4 (API max)
    const batches = _.chunk(normalised, 4);

    const batchResults = await Promise.allSettled(
        batches.map(async batch => {
            const provider = batch[0].provider as IProvider;
            const result = await store.dispatch(
                CustomersController.testInsuranceV2({
                    npi: +provider.npi,
                    spi: +provider.spi,
                    testClaimBillings: batch.map(item => ({
                        ndc: 'placeholder',
                        medid: String(item.medid),
                        quantity: item.copayQuantity ?? 30,
                        daysSupply: item.copayDaysSupply ?? 30
                    }))
                })
            );

            if (result.meta.requestStatus !== 'fulfilled') {
                throw new Error('request failed');
            }

            const payload = result.payload as ICustomersTestInsuranceResponse;
            return payload.patient.fills.map(fill => mapFillToSlot(fill));
        })
    );

    const batchCount = batchResults.filter(r => r.status === 'fulfilled').length;
    const slots = batchResults.flatMap((r, i) =>
        r.status === 'fulfilled' ? r.value : batches[i].map(() => ({ coPay: null, status: 'ERROR' as TestClaimStatus, rejectCodes: [] }))
    );
    return { slots, batchCount };
};

// ---------------------------------------------------------------------------
// Core: run and merge claims for a given prescription list
// ---------------------------------------------------------------------------

interface RunClaimsOptions {
    /** All insurances currently on file (used to build combo key) */
    insurances: ICustomersInsuranceManualResponse[];
    /** Prescriptions to test — pass [firstPrescription] for situation 1, all for situation 2 */
    prescriptions: ITestClaimTransferPrescriptions[];
    customer?: ICustomersGetResponse | null;
}

const runAndMergeClaims = async ({ insurances, prescriptions, customer }: RunClaimsOptions): Promise<ITestClaimsByCombo> => {
    const user = customer ?? store.getState().onboarding.user;
    if (!user?.userOnboarding) return {};

    const comboKey = buildComboKey(insurances);

    const existingComboMap: ITestClaimsByCombo =
        (user.userOnboarding.details as unknown as { testClaimsByCombo?: ITestClaimsByCombo })?.testClaimsByCombo ?? {};
    const existingSlots: Record<string, ITestClaimSlot> = existingComboMap[comboKey] ?? {};

    const prescriptionCopayQuantities = (
        user.userOnboarding.details as unknown as {
            prescriptionCopayQuantities?: Record<string, { quantity: number; daysSupply: number }>;
        }
    )?.prescriptionCopayQuantities as Record<string, { quantity: number; daysSupply: number }> | undefined;

    // Determine which prescriptions need a fresh request
    const toRerun = prescriptions.filter(p => {
        const slotKey = buildSlotKey(p, prescriptionCopayQuantities);
        const existing = existingSlots[slotKey];
        return !existing || shouldRerun(existing);
    });
    // Run a single test claim — the server runs it against all of the user's insurances
    const newSlots: Record<string, ITestClaimSlot> = {};

    let totalBatchCount = 0;
    if (toRerun.length > 0) {
        const { slots: fills, batchCount } = await runTestClaimForPrescriptions(toRerun, prescriptionCopayQuantities, insurances);
        totalBatchCount = batchCount;
        fills.forEach((slot, idx) => {
            const prescription = toRerun[idx];
            if (!prescription) return;
            newSlots[buildSlotKey(prescription, prescriptionCopayQuantities)] = slot;
        });
    }

    // Build updated slots: preserve ALL previously-run slots, overlay new results for current keys
    const currentSlotKeys = new Set(prescriptions.map(p => buildSlotKey(p, prescriptionCopayQuantities)));
    const updatedSlots: Record<string, ITestClaimSlot> = { ...existingSlots };

    for (const slotKey of currentSlotKeys) {
        if (newSlots[slotKey]) {
            updatedSlots[slotKey] = newSlots[slotKey];
        }
    }
    const updatedComboMap: ITestClaimsByCombo = {
        ...existingComboMap, // preserve all other combo keys
        [comboKey]: updatedSlots // replace only current combo
    };

    if (Object.keys(newSlots).length) {
        // Persist only if we have new slots to avoid additional updates...
        const currentCount = (user.userOnboarding.details as unknown as { copayRequestCount?: number })?.copayRequestCount ?? 0;
        await store.dispatch(
            CustomersController.updateMetadata({
                ...user.userOnboarding,
                details: {
                    ...user.userOnboarding.details,
                    testClaimsByCombo: updatedComboMap,
                    copayRequestCount: currentCount + totalBatchCount
                } as unknown as typeof user.userOnboarding.details
            })
        );
    }

    await store.dispatch(CustomersController.get());
    return updatedComboMap;
};

// ---------------------------------------------------------------------------
// Situation 1 — Insurance eligibility gate
// Call after insurance add/remove, or after first prescription is added.
// Returns true if the user is covered by the pharmacy.
// ---------------------------------------------------------------------------

export const runEligibilityGateClaim = async (): Promise<boolean> => {
    const user = store.getState().onboarding.user;
    if (!user?.userOnboarding) return true;

    const copayPrescriptions = getCopayEligiblePrescriptions(user.transferPrescriptions) as ITestClaimTransferPrescriptions[];
    const firstPrescription = copayPrescriptions[0];
    const insurances = user.insurances;
    const prescriptionCopayQuantities = (
        user.userOnboarding.details as unknown as {
            prescriptionCopayQuantities?: Record<string, { quantity: number; daysSupply: number }>;
        }
    )?.prescriptionCopayQuantities as Record<string, { quantity: number; daysSupply: number }> | undefined;

    if (!firstPrescription || !insurances.length) {
        return true;
    }

    const updatedComboMap = await runAndMergeClaims({
        insurances,
        prescriptions: [firstPrescription]
    });

    const comboKey = buildComboKey(insurances);
    const slots = updatedComboMap[comboKey] ?? {};
    const firstSlotKey = buildSlotKey(firstPrescription, prescriptionCopayQuantities);
    const slot = slots[firstSlotKey];
    const insuranceUploadsLength = user.insuranceUploads?.length ?? 0;

    if (!slot) return true; // no result (e.g. server error on first attempt) — don't block
    return isEligibleForPharmacy(slot, insuranceUploadsLength);
};

// ---------------------------------------------------------------------------
// Situation 2 — Full copay display
// Call on explicit user action. Returns slots for all current prescriptions.
// ---------------------------------------------------------------------------

export const runFullCopayClaims = async (customer?: ICustomersGetResponse | null): Promise<Record<string, ITestClaimSlot>> => {
    const user = customer ?? store.getState().onboarding.user;
    if (!user?.userOnboarding) return {};

    const copayPrescriptions = getCopayEligiblePrescriptions(user.transferPrescriptions) as ITestClaimTransferPrescriptions[];
    if (!copayPrescriptions.length) return {};

    const insurances = user.insurances;
    if (!insurances.length) return {};

    // If limit is reached, return whatever slots we already have without making new requests
    if (isCopayRequestLimitReached(user)) {
        const existingComboMap =
            (user.userOnboarding.details as unknown as { testClaimsByCombo?: ITestClaimsByCombo })?.testClaimsByCombo ?? {};
        const comboKey = buildComboKey(insurances);
        return existingComboMap[comboKey] ?? {};
    }

    const updatedComboMap = await runAndMergeClaims({
        insurances,
        prescriptions: copayPrescriptions,
        customer
    });

    const comboKey = buildComboKey(insurances);
    return updatedComboMap[comboKey] ?? {};
};

// ---------------------------------------------------------------------------
// OTC pricing
// ---------------------------------------------------------------------------

const getCostForSingleOtc = async (otc: ITransferPrescriptions): Promise<{ medid: string; cost: null | number }> => {
    const res = await store.dispatch(
        CustomersController.getOtcPriceByQuantityMedId({
            medid: otc.medid,
            quantity: otc.quantity
        })
    );
    const cost = res.meta.requestStatus === 'fulfilled' && typeof res.payload === 'number' ? res.payload : null;
    return { medid: otc.medid, cost };
};

export const getOtcCostsByQuantity = async (otcRxs: ITransferPrescriptions[]): Promise<{ medid: string; cost: number | null }[]> => {
    const results = await Promise.all(otcRxs.map(getCostForSingleOtc));
    return results.filter(item => item.cost !== null);
};
